import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const categories = [
  'Advertising',
  'Stationery & Cards',
  'Packaging',
  'Office Supplies',
  'Restaurants & Hotels',
  'Book Printing',
  'Flexible Materials',
  'Adhesive Advertising',
  'Advertising Panels',
  'Outdoor Displays',
  'Other',
];

export function QuotePage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();

  const [formData, setFormData] = useState({
    product_category: '',
    description: '',
    quantity: '',
    deadline: '',
    contact_email: user?.email || '',
    contact_phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.product_category || !formData.description || !formData.quantity || !formData.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      await api.post('/api/quotes', {
        ...formData,
        quantity: parseInt(formData.quantity),
      });

      setSubmitted(true);
      toast.success('Quote request submitted!');
    } catch {
      // Submit failed
      toast.error('Failed to submit quote request');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-lg border border-gray-200"
        >
          <CheckCircle className="h-16 w-16 mx-auto text-mint mb-6" />
          <h1 className="text-2xl font-heading font-bold text-navy">Quote Request Submitted!</h1>
          <p className="text-gray-500 mt-2">
            {"We've received your quote request. Our team will get back to you within 24 hours with a personalized quote."}
          </p>
          <div className="flex gap-4 justify-center mt-6">
            <Button
              onClick={() => navigate('/products')}
              variant="outline"
              className="rounded-full"
            >
              Browse Products
            </Button>
            {user && (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-mint hover:bg-mint-dark text-black rounded-full"
              >
                View Dashboard
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="text-sm text-coral uppercase tracking-[0.2em] font-semibold">
            Bulk Orders
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-heading font-bold text-navy">
            Get a Custom Quote
          </h1>
          <p className="mt-4 text-gray-600 max-w-xl mx-auto">
            {"Have a large order or specific requirements? Fill out the form below and we'll provide you with a personalized quote."}
          </p>
        </motion.div>

        <form onSubmit={handleSubmit}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-200 space-y-6"
          >
            <div className="flex items-center gap-3 pb-6 border-b border-gray-200">
              <div className="w-12 h-12 bg-mint/10 rounded-xl flex items-center justify-center">
                <FileText className="h-6 w-6 text-mint" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-navy">Quote Request Form</h2>
                <p className="text-sm text-gray-500">Fill in your requirements</p>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid gap-6">
              <div>
                <Label className="text-navy font-medium">Product Category *</Label>
                <Select
                  value={formData.product_category}
                  onValueChange={(val) => handleSelectChange('product_category', val)}
                >
                  <SelectTrigger className="mt-2" data-testid="quote-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description" className="text-navy font-medium">
                  Description *
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe what you need: size, paper type, colors, finishing options..."
                  rows={4}
                  className="mt-2"
                  required
                  data-testid="quote-description-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="quantity" className="text-navy font-medium">
                    Quantity *
                  </Label>
                  <Input
                    id="quantity"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleChange}
                    placeholder="e.g., 1000"
                    className="mt-2"
                    min="1"
                    required
                    data-testid="quote-quantity-input"
                  />
                </div>

                <div>
                  <Label htmlFor="deadline" className="text-navy font-medium">
                    Deadline (Optional)
                  </Label>
                  <Input
                    id="deadline"
                    name="deadline"
                    type="date"
                    value={formData.deadline}
                    onChange={handleChange}
                    className="mt-2"
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="quote-deadline-input"
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="font-medium text-navy mb-4">Contact Information</h3>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="contact_email" className="text-navy font-medium">
                      Email *
                    </Label>
                    <Input
                      id="contact_email"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="mt-2"
                      required
                      data-testid="quote-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_phone" className="text-navy font-medium">
                      Phone (Optional)
                    </Label>
                    <Input
                      id="contact_phone"
                      name="contact_phone"
                      type="tel"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      placeholder="+961 XX XXX XXX"
                      className="mt-2"
                      data-testid="quote-phone-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-mint hover:bg-mint-dark text-black py-6 rounded-full btn-hover"
              data-testid="submit-quote-btn"
            >
              {submitting ? 'Submitting...' : 'Request Quote'}
              {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </motion.div>
        </form>
      </div>
    </div>
  );
}
